import { FeatureFlag } from "@hex/common";
import {
  LDClient,
  LDFlagChangeset,
  LDFlagSet,
  LDFlagValue,
} from "launchdarkly-js-client-sdk";
import {
  ReactSdkContext,
  defaultReactOptions,
} from "launchdarkly-react-client-sdk";
import React, { type JSX, useRef, useState } from "react";

import { useOnMount } from "../../hooks/useOnMount";

export const ALWAYS_RERENDER = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  current,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  previous,
}: {
  current: LDFlagValue;
  previous: LDFlagValue;
}): true => true;

export type LiveFlagConfig = Partial<
  Record<
    FeatureFlag,
    {
      shouldRerender: ({
        current,
        previous,
      }: {
        current: LDFlagValue;
        previous: LDFlagValue;
      }) => boolean;
    }
  >
>;

/**
 * Number of seconds we wait for the client to initialize before moving on.
 * This doesn't stop the client from actually initializing, but might result
 * in a user getting the default flag value.
 *
 * If the client has bootstrap flags then this is moot, because the client
 * will consider itself initialized immediately.
 */
const INITIALIZATION_TIMEOUT = 1;

const LIVE_UPDATE_CONFIG: LiveFlagConfig = {
  "auto-refresh-client-config": { shouldRerender: ALWAYS_RERENDER },
};

const REACT_CONTEXT = defaultReactOptions.reactContext;

export type LaunchDarklyProviderConfig = {
  /**
   * Singleton client used by all LD functionality in the front-end.
   * See global-constants.ts.
   */
  ldClient: LDClient;
};

/**
 * React component that initializes LaunchDarkly's JS SDK (`launchdarkly-js-client-sdk`)
 * and provides flags to the component tree below it.
 *
 * Initial flag state is seeded from `ldClient.allFlags()` (typically bootstrap data),
 * so there is no loading gap. Async initialization completes in the background and
 * updates flags once ready.
 *
 * This component is custom, but it provides a standard LD React context used by the
 * LaunchDarkly React SDK hooks, so all existing useFlags() calls continue to work.
 */
// Module-level guard: the LD SDK has no removeHook API, so we can only safely
// register the afterIdentify hook once per page load.
let hookRegistered = false;

export default function HexLaunchDarklyProvider({
  children,
  ldClient,
}: React.PropsWithChildren<LaunchDarklyProviderConfig>): JSX.Element | null {
  const [initialized, setInitialized] = useState(false);
  const [ldData, setLDData] = useState<ReactSdkContext>(() => ({
    flags: {},
    flagKeyMap: {},
    ldClient,
    error: undefined,
  }));

  // Kinda silly, but LD requires that JSON variants all be the same top-level type.
  // Below, this means that "*" only has the intended effect (force updates on all flags)
  // if it is the only member of the array.
  const flagsToForceUpdatesRef = useRef<FeatureFlag[] | ["*"]>([]);

  // ldClient is a stable singleton (see global-constants.ts), LIVE_UPDATE_CONFIG
  // and timeout are mount-time config — intentionally not reactive.

  useOnMount(() => {
    // Guard against state updates after unmount (e.g. React Strict Mode
    // double-invocation, or the component being torn down before
    // waitForInitialization settles).
    let cancelled = false;
    let extraCleanup: (() => void) | null = null;

    function forceUpdateEveryFlag(): boolean {
      return (
        flagsToForceUpdatesRef.current.length === 1 &&
        flagsToForceUpdatesRef.current[0] === "*"
      );
    }

    void ldClient
      .waitForInitialization(INITIALIZATION_TIMEOUT)
      .then(() => {
        if (cancelled) return;
        flagsToForceUpdatesRef.current = ldClient.variation(
          "force-live-updates-for-flags",
          [],
        );
        setLDData((prev) => ({ ...prev, flags: ldClient.allFlags() }));
        setInitialized(true);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setLDData((prev) => ({ ...prev, error: e }));
        setInitialized(true);

        // Only subscribe to ready and failed if waitForInitialization timed out
        // because we want the introduction of init timeout to be as minimal and
        // backwards compatible as possible.
        if (e.name.toLowerCase().includes("timeout")) {
          function onReady(): void {
            if (cancelled) return;
            flagsToForceUpdatesRef.current = ldClient.variation(
              "force-live-updates-for-flags",
              [],
            );
            setLDData((prevState) => ({
              ...prevState,
              flags: ldClient.allFlags(),
            }));
          }

          function onFailed(err: Error): void {
            if (cancelled) return;
            setLDData((prevState) => ({ ...prevState, error: err }));
          }

          ldClient.on("failed", onFailed);
          ldClient.on("ready", onReady);
          extraCleanup = () => {
            ldClient.off("failed", onFailed);
            ldClient.off("ready", onReady);
          };
        }
      });

    function onChange(changes: LDFlagChangeset): void {
      const updates: LDFlagSet = {};

      if (forceUpdateEveryFlag()) {
        for (const [key, { current }] of Object.entries(changes)) {
          updates[key] = current;
        }
      } else {
        if (LIVE_UPDATE_CONFIG) {
          for (const [key, { current, previous }] of Object.entries(changes)) {
            const keyUpdateConfig = LIVE_UPDATE_CONFIG[key as FeatureFlag];
            try {
              if (keyUpdateConfig?.shouldRerender({ current, previous })) {
                updates[key] = current;
              }
            } catch (err) {
              console.error(
                "[HexLaunchDarkly] shouldRerender callback failed",
                err,
              );
            }
          }
        }

        for (const key of flagsToForceUpdatesRef.current) {
          const change = changes[key];
          if (change != null) {
            updates[key] = change.current;
          }
        }
      }

      if (Object.keys(updates).length > 0) {
        setLDData((prevState) => {
          return {
            ...prevState,
            flags: {
              ...prevState.flags,
              ...updates,
            },
          };
        });
      }
    }

    ldClient.on("change", onChange);

    // The LD SDK has no removeHook API, so guard with a module-level flag to
    // ensure the hook is registered at most once across all mounts (including
    // React Strict Mode's double-invocation). Without this, every remount adds
    // another afterIdentify callback that fires setLDData redundantly.
    if (!hookRegistered) {
      hookRegistered = true;
      ldClient.addHook({
        getMetadata: () => {
          return { name: "force-refresh-flags" };
        },
        afterIdentify: (_hookContext, data, _result) => {
          setLDData((prevState) => {
            return {
              ...prevState,
              flags: ldClient.allFlags(),
            };
          });
          return data;
        },
      });
    }

    // Specific flag events always fire before the more general 'change' event
    // https://github.com/launchdarkly/js-sdk-common/blob/0422c50753e9cb3e51b2c9faf5d5c99ea3ea7b0b/src/index.js#L557C9-L557C21
    function onForceLiveUpdatesChange(current: FeatureFlag[] | ["*"]): void {
      flagsToForceUpdatesRef.current = current;

      // Force-update the provider state with the current cached data for these
      // flags, just so we're not dependant on someone toggling a flag after updating
      // this configuration.
      if (forceUpdateEveryFlag()) {
        setLDData((prevState) => {
          return {
            ...prevState,
            flags: ldClient.allFlags(),
          };
        });
      } else {
        const stateUpdates: LDFlagSet = {};

        for (const flag of flagsToForceUpdatesRef.current) {
          // returns undefined if no flag data
          const currentValue = ldClient.variation(flag);
          stateUpdates[flag] = currentValue;
        }

        setLDData((prevState) => {
          return {
            ...prevState,
            flags: {
              ...prevState.flags,
              ...stateUpdates,
            },
          };
        });
      }
    }

    ldClient.on(
      "change:force-live-updates-for-flags",
      onForceLiveUpdatesChange,
    );

    return function cleanup() {
      cancelled = true;
      ldClient.off("change", onChange);
      ldClient.off(
        "change:force-live-updates-for-flags",
        onForceLiveUpdatesChange,
      );
      extraCleanup?.();
    };
  });

  if (!initialized) {
    return null;
  }

  return <REACT_CONTEXT value={ldData}>{children}</REACT_CONTEXT>;
}
